import {
  EmailDraft,
  Flow,
  Layout,
  Settings,
  currencySymbolFor,
} from './types';

// Neutral gray shown behind product images while they load or when the
// image URL is missing. Reused by the live preview so the two stay in sync.
export const PRODUCT_IMAGE_PLACEHOLDER_BG = '#f3f4f6';

// Klaviyo template expressions for the product list and magic-cart link.
type RecommendationExprs = {
  cart: string;
  link: string;
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function bodyToHtml(text: string): string {
  return escapeHtml(text).replace(/\r?\n/g, '<br>');
}

export function buttonTextForFlow(flow: Flow, settings: Settings): string {
  if (flow === 'subscription') {
    return settings.subscriptionButtonText;
  }
  if (flow === 'cross-sell') {
    return settings.crossSellButtonText;
  }
  return settings.replenishmentButtonText;
}

// Decides which Klaviyo variables the template reads from. Only cross-sell
// emails can opt into the profile property; every other flow uses the
// prediction event, which is the only source carrying product objects.
function recommendationExprs(flow: Flow, settings: Settings): RecommendationExprs {
  const useProfileProperty = flow === 'cross-sell'
    && settings.crossSellRecommendationSource === 'crossell';

  if (useProfileProperty) {
    return {
      cart: "person|lookup:'Relo Crossell Magic Cart'",
      link: "person|lookup:'Relo Crossell Magic Cart Link'",
    };
  }

  return { cart: 'event.bp_cart', link: 'event.bp_magic_cart_link' };
}

// The shop/cart button, hidden by Klaviyo when there is no magic-cart link.
// Shared by the full email and the standalone products block.
function ctaRow(settings: Settings, link: string, ctaText: string): string {
  const { primaryColor, buttonTextColor, buttonBorderRadius } = settings;

  return `{% if ${link} %}
          <tr>
            <td align="center" style="padding: 16px 32px 36px;">
              <a href="{{ ${link} }}"
                style="display: inline-block; background-color: ${primaryColor};
                  color: ${buttonTextColor}; padding: 12px 28px;
                  border-radius: ${buttonBorderRadius}px; font-weight: 600;
                  font-size: 15px; text-decoration: none;">${ctaText}</a>
            </td>
          </tr>
          {% endif %}`;
}

function productBlock(layout: Layout, settings: Settings, cart: string): string {
  const { textColor, productImageMaxHeightDesktop } = settings;
  const imgFull = 'display: block; border: 0; width: 100%; aspect-ratio: 1 / 1;'
    + ` max-height: ${productImageMaxHeightDesktop}px; object-fit: cover;`
    + ` background: ${PRODUCT_IMAGE_PLACEHOLDER_BG};`;

  // Klaviyo renders Django templates, so prices are formatted with the
  // floatformat filter and prefixed with the merchant's currency symbol.
  const currencySymbol = escapeHtml(currencySymbolFor(settings.currencyCode));
  const price = (expr: string): string => `${currencySymbol}{{ ${expr}|floatformat:2 }}`;

  if (layout === 'grid') {
    return `
          <tr>
            <td style="padding: 8px 32px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  {% for product in ${cart}|slice:':3' %}
                  <td width="33%" style="vertical-align: top; padding: 0 6px;">
                    <img src="{{ product.imageSrc }}" alt="{{ product.name }}"
                      style="${imgFull}">
                    <p style="margin: 8px 0 4px; font-weight: 600; font-size: 14px; color: ${textColor};">
                      {{ product.name }}</p>
                    <p style="margin: 0; font-size: 13px; color: ${textColor};">${price('product.price')}</p>
                  </td>
                  {% endfor %}
                </tr>
              </table>
            </td>
          </tr>`;
  }

  if (layout === 'hero-supporting') {
    // Klaviyo's Django subset has no {% with %} and can't index a |lookup
    // result, so the hero and supporting rows are sliced from the same list:
    // the first item is the hero, items 2-3 are the supporting pair.
    return `
          {% for product in ${cart}|slice:':1' %}
          <tr>
            <td style="padding: 8px 32px;">
              <img src="{{ product.imageSrc }}" alt="{{ product.name }}"
                style="${imgFull}">
              <p style="margin: 8px 0 4px; font-weight: 600; font-size: 16px; color: ${textColor};">
                {{ product.name }}</p>
              <p style="margin: 0 0 16px; font-size: 14px; color: ${textColor};">${price('product.price')}</p>
            </td>
          </tr>
          {% endfor %}
          {% if ${cart}|slice:'1:3' %}
          <tr>
            <td style="padding: 0 32px 8px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  {% for product in ${cart}|slice:'1:3' %}
                  <td width="50%" style="vertical-align: top; padding: 0 4px;">
                    <img src="{{ product.imageSrc }}" alt="{{ product.name }}"
                      style="${imgFull}">
                    <p style="margin: 8px 0 4px; font-weight: 600; font-size: 14px; color: ${textColor};">
                      {{ product.name }}</p>
                    <p style="margin: 0; font-size: 13px; color: ${textColor};">${price('product.price')}</p>
                  </td>
                  {% endfor %}
                </tr>
              </table>
            </td>
          </tr>
          {% endif %}`;
  }

  if (layout === 'horizontal-card') {
    return `
          {% for product in ${cart}|slice:':3' %}
          <tr>
            <td style="padding: 8px 32px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  <td width="120" style="vertical-align: top; padding-right: 16px;">
                    <img src="{{ product.imageSrc }}" alt="{{ product.name }}"
                      width="120" height="120"
                      style="display: block; border: 0; width: 120px; height: 120px;
                        object-fit: cover; background: ${PRODUCT_IMAGE_PLACEHOLDER_BG};">
                  </td>
                  <td style="vertical-align: middle;">
                    <p style="margin: 0 0 4px; font-weight: 600; font-size: 15px; color: ${textColor};">
                      {{ product.name }}</p>
                    <p style="margin: 0; font-size: 14px; color: ${textColor};">${price('product.price')}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          {% endfor %}`;
  }

  return `
          {% for product in ${cart}|slice:':3' %}
          <tr>
            <td style="padding: 8px 32px;">
              <img src="{{ product.imageSrc }}" alt="{{ product.name }}"
                style="${imgFull}">
              <p style="margin: 8px 0 4px; font-weight: 600; font-size: 15px; color: ${textColor};">
                {{ product.name }}</p>
              <p style="margin: 0; font-size: 14px; color: ${textColor};">${price('product.price')}</p>
            </td>
          </tr>
          {% endfor %}`;
}

function generateEmailHtml(
  draft: EmailDraft,
  layout: Layout,
  settings: Settings,
  flow: Flow,
): string {
  const { backgroundColor, textColor } = settings;

  const subject = escapeHtml(draft.subject);
  const previewText = escapeHtml(draft.previewText);
  const headline = escapeHtml(draft.headline);
  const bodyHtml = bodyToHtml(draft.body);
  const ctaText = escapeHtml(buttonTextForFlow(flow, settings) || 'Shop now');

  const previewSection = previewText
    ? `  <div style="display: none; max-height: 0; overflow: hidden;">${previewText}</div>\n\n`
    : '';

  const { cart, link } = recommendationExprs(flow, settings);
  const products = productBlock(layout, settings, cart);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subject}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; background-color: #f9fafb;">

${previewSection}  <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
    <tr>
      <td align="center" style="padding: 20px 16px;">
        <table width="600" cellpadding="0" cellspacing="0" border="0" role="presentation"
          style="max-width: 600px; width: 100%; background-color: ${backgroundColor};">

          <tr>
            <td style="padding: 28px 32px 16px; color: ${textColor}; font-size: 15px; line-height: 1.6;">
              <h2 style="margin: 0 0 16px; font-size: 24px; font-weight: 700;
                line-height: 1.3; color: ${textColor};">${headline}</h2>
              <p style="margin: 0 0 24px;">${bodyHtml}</p>
            </td>
          </tr>
          ${products}

          ${ctaRow(settings, link, ctaText)}

        </table>
      </td>
    </tr>
  </table>

</body>
</html>`;
}

// Just the dynamic recommendation block — product cards plus the shop/cart
// button — wrapped in a standalone table. No headline, body, or document
// shell, so it can be pasted into an existing Klaviyo email/template.
export function generateProductBlockHtml(
  layout: Layout,
  settings: Settings,
  flow: Flow,
): string {
  const { backgroundColor } = settings;
  const ctaText = escapeHtml(buttonTextForFlow(flow, settings) || 'Shop now');
  const { cart, link } = recommendationExprs(flow, settings);
  const products = productBlock(layout, settings, cart);

  return `<table width="600" cellpadding="0" cellspacing="0" border="0" role="presentation"
  style="max-width: 600px; width: 100%; background-color: ${backgroundColor};">
${products}

          ${ctaRow(settings, link, ctaText)}
        </table>`;
}

export default generateEmailHtml;
